import { supabase } from './supabaseService';
import { configService } from './configService';
import { logger } from '../utils/logger';

interface ConnectionStatus {
  service: string;
  isConnected: boolean;
  lastChecked: Date;
  error?: string;
}

class ConnectionMonitor {
  public checkInterval: number = 5 * 60 * 1000; // 5 minutes
  private intervalId: NodeJS.Timeout | null = null;
  private connectionStatuses: Map<string, ConnectionStatus> = new Map();

  // Start monitoring all connections
  startMonitoring(): void {
    logger.debug('Iniciando monitoreo de conexiones...');
    
    // Initial check
    this.checkAllConnections();
    
    // Set up periodic checks
    this.intervalId = setInterval(() => {
      this.checkAllConnections();
    }, this.checkInterval);
    
    logger.info(`Monitoreo iniciado - verificando cada ${this.checkInterval / 1000 / 60} minutos`);
  }

  // Stop monitoring
  stopMonitoring(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
      console.log('🛑 Monitoreo de conexiones detenido');
    }
  }

  // Check all service connections
  private async checkAllConnections(): Promise<void> {
    logger.debug('Verificando estado de todas las conexiones...');
    
    const checks = [
      this.checkSupabaseConnection(),
      this.checkFirebaseConnection()
    ];

    await Promise.allSettled(checks);
    
    // Log summary
    const connectedServices = Array.from(this.connectionStatuses.values())
      .filter(status => status.isConnected).length;
    const totalServices = this.connectionStatuses.size;
    
    logger.debug(`Estado de conexiones: ${connectedServices}/${totalServices} servicios conectados`);
  }

  // Check Supabase connection
  private async checkSupabaseConnection(): Promise<void> {
    try {
      logger.debug('Verificando conexión Supabase...');
      
      // Simple query to test connection
      const { error } = await supabase
        .from('users')
        .select('id')
        .limit(1);

      if (error) {
        throw new Error(`Supabase error: ${error.message}`);
      }

      await this.updateConnectionStatus('supabase', true);
      logger.info('Supabase conectado correctamente');
    } catch (error) {
      logger.error('Error conexión Supabase', error);
      await this.updateConnectionStatus('supabase', false, error instanceof Error ? error.message : String(error));
    }
  }

  // Check Firebase connection
  private async checkFirebaseConnection(): Promise<void> {
    try {
      logger.debug('Verificando conexión Firebase...');
      
      // Use the same method that actually works
      const firebaseConfig = configService.getFirebaseConfig();
      const isConfigured = !!(firebaseConfig?.apiKey && firebaseConfig?.projectId);
      
      if (!isConfigured) {
        await this.updateConnectionStatus('firebase', false, 'No configurado');
        logger.debug('Firebase no configurado en ConnectionMonitor');
        return;
      }

      const isConnected = await configService.testFirebaseConnection();

      if (isConnected) {
        await this.updateConnectionStatus('firebase', true);
        logger.info('Firebase conectado correctamente');
      } else {
        await this.updateConnectionStatus('firebase', false, 'Credenciales inválidas');
        logger.warn('Firebase test falló en ConnectionMonitor');
      }
    } catch (error) {
      logger.error('Error conexión Firebase', error);
      await this.updateConnectionStatus('firebase', false, error instanceof Error ? error.message : String(error));
    }
  }


  // Update connection status
  private async updateConnectionStatus(service: string, isConnected: boolean, error?: string): Promise<void> {
    this.connectionStatuses.set(service, {
      service,
      isConnected,
      lastChecked: new Date(),
      error
    });

    // Emit custom event for UI components to listen
    window.dispatchEvent(new CustomEvent('connection-status-changed', {
      detail: {
        service,
        isConnected,
        error
      }
    }));

    // Persist health status to DB (best effort, non-blocking). Solo lo
    // intentamos si el usuario tiene rol admin/superadmin: integration_health
    // es una tabla admin-only y colaboradores recibían un 403 ruidoso.
    const { default: authService } = await import('./authService');
    if (authService.getCurrentSession()?.user?.role !== 'superadmin') return;
    const { systemHealthService } = await import('./systemHealthService');
    await systemHealthService.updateIntegrationHealth(service, isConnected ? 'healthy' : 'failed', undefined, error);
  }

  // Get current status of all connections
  getConnectionStatuses(): ConnectionStatus[] {
    return Array.from(this.connectionStatuses.values());
  }

  // Get status of specific service
  getServiceStatus(service: string): ConnectionStatus | null {
    return this.connectionStatuses.get(service) || null;
  }

  // Force check of specific service
  async checkService(service: string): Promise<void> {
    switch (service) {
      case 'supabase':
        await this.checkSupabaseConnection();
        break;
      case 'firebase':
        await this.checkFirebaseConnection();
        break;
      default:
        throw new Error(`Comprobación no disponible para ${service}`);
    }
    const status = this.getServiceStatus(service);
    if (!status?.isConnected) throw new Error('No se pudo confirmar la conexión');

  }

  // Get overall health status
  getOverallHealth(): { healthy: boolean; connectedCount: number; totalCount: number } {
    const statuses = Array.from(this.connectionStatuses.values());
    const connectedCount = statuses.filter(s => s.isConnected).length;
    const totalCount = statuses.length;
    
    return {
      healthy: connectedCount === totalCount,
      connectedCount,
      totalCount
    };
  }
}

// Export singleton instance
export const connectionMonitor = new ConnectionMonitor();